import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cn from "../../../utils/cn";
import { noop } from "lodash";
import filterChildren from "../../utils/Children/filterChildren";
import {
  SPACEBAR_KEY_CODE,
  RETURN_KEY_CODE,
  ESC_KEY_CODE,
  UP_ARROW_KEY_CODE,
  DOWN_ARROW_KEY_CODE,
  TAB_KEY_CODE,
} from "../../../utils/Browser/keyCodes";
import { isKeyPressed } from "../../../utils/isKeyPressed";

const Menu = ({
  anchorElement,
  className,
  closeMainMenu = noop,
  id = "",
  position = "bottom-start",
  style = {},
  subItemParent = false,
  target = "top-bottom",
  onClick = noop,
  children,
  keyHandler = noop,
}) => {
  const [menuState, setMenuState] = useState({
    position: 0,
    isOpen: false,
    menuItemLength: 0,
  });

  const menuDomRef = useRef(null);

  useEffect(() => {
    const handleDocumentKeyDown = (event) => {
      if (isKeyPressed(event, "Escape", ESC_KEY_CODE) && menuState.isOpen) {
        event.preventDefault();
        setMenuState({ ...menuState, isOpen: false, position: 0 });
      }
    };

    const handleDocumentClick = (event) => {
      if (
        menuState.isOpen &&
        menuDomRef.current &&
        !menuDomRef.current.contains(event.target)
      ) {
        setMenuState({ ...menuState, isOpen: false, position: 0 });
      }
    };

    const handleDocumentFocus = (event) => {
      const isWindowEvent = event.target.console;
      const clickedOutsideOfMenuDom =
        event.target &&
        !isWindowEvent &&
        menuDomRef.current &&
        !menuDomRef.current.contains(event.target);
      if (menuState.isOpen && clickedOutsideOfMenuDom) {
        setMenuState({ ...menuState, isOpen: false, position: 0 });
      }
    };

    document.addEventListener("keydown", handleDocumentKeyDown, false);
    document.addEventListener("click", handleDocumentClick, false);
    window.addEventListener("focus", handleDocumentFocus, true);

    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("focus", handleDocumentFocus);
    };
  }, [menuState]);

  useEffect(() => {
    if (menuState.isOpen) {
      const firstFocusableElement = subItemParent
        ? menuDomRef.current.querySelectorAll('[role="button"]')[1]
        : menuDomRef.current.querySelectorAll('[role="button"]')[0];

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }, [menuDomRef.current, menuState.isOpen]);

  const togglePopover = () => {
    setMenuState({ ...menuState, isOpen: !menuState.isOpen, position: 0 });
  };

  const setPositionOnKeyDown = (event) => {
    const menuItemLength =
      menuState.menuItemLength || filterChildren(children).length;
    const isLastMenuItem = menuState.position === menuItemLength - 1;

    if (
      isKeyPressed(event, "Tab", TAB_KEY_CODE) &&
      menuState.isOpen &&
      menuItemLength > 0 &&
      !isLastMenuItem
    ) {
      event.preventDefault();
      setMenuState({ ...menuState, position: menuState.position + 1 });
      return true;
    }

    if (
      isKeyPressed(event, "ArrowDown", DOWN_ARROW_KEY_CODE) &&
      menuState.isOpen
    ) {
      event.preventDefault();
      setMenuState({
        ...menuState,
        position: (menuState.position + 1) % menuItemLength,
      });
      return true;
    }

    if (isKeyPressed(event, "ArrowUp", UP_ARROW_KEY_CODE) && menuState.isOpen) {
      event.preventDefault();
      setMenuState(
        menuState.position === 0
          ? { ...menuState, position: menuItemLength - 1 }
          : { ...menuState, position: menuState.position - 1 }
      );
      return true;
    }

    return false;
  };

  const onKeyDown = (event) => {
    keyHandler(event);

    if (
      (isKeyPressed(event, "Enter", RETURN_KEY_CODE) ||
        isKeyPressed(event, " ", SPACEBAR_KEY_CODE)) &&
      !menuState.isOpen
    ) {
      return togglePopover();
    }

    return setPositionOnKeyDown(event);
  };

  const setPosition = (position) => {
    setMenuState({ ...menuState, position });
  };

  const setMenuItemLength = (count) => {
    setMenuState({ ...menuState, menuItemLength: count });
  };

  const listClass = cn(
    "bg-white shadow-menu whitespace-no-wrap transition-transform duration-500 opacity-100 z-popover visible"
  );

  const menuClass = cn("text-brand-blue relative inline-block", className);

  const menuListClass = cn(listClass, {
    "opacity-0 invisible": !menuState.isOpen,
    "absolute top-124 right-24": position === "notification",
    "absolute top-full left-0": position === "bottom-start",
    "absolute top-full right-full": position === "bottom-end",
    "absolute bottom-full left-0": position === "top-start",
    "absolute right-full bottom-full": position === "top-end",
    "absolute top-0 left-full": position === "right-start",
    "absolute bottom-0 left-full": position === "right-end",
    "absolute top-0 right-full": position === "left-start",
    "absolute right-full bottom-0": position === "left-end",
    "transform translate-y-0": target === "top-bottom",
    "transform -translate-y-5": !menuState.isOpen && target === "top-bottom",
    "transform translate-y-0": target === "bottom-top",
    "transform translate-y-5": !menuState.isOpen && target === "bottom-top",
    "transform translate-x-0": target === "right-left",
    "transform translate-x-5'": !menuState.isOpen && target === "right-left",
    "transform translate-x-0": target === "left-right",
    "transform -translate-x-5": !menuState.isOpen && target === "left-right",
  });

  const filteredChildren = filterChildren(children);

  const childrenWithFuncs = React.Children.map(
    filteredChildren,
    (child, index) =>
      React.cloneElement(child, {
        closeMainMenu,
        handleMenuItemNavigation: onKeyDown,
        index,
        position: menuState.position,
        setMenuItemLength,
        setPosition,
        togglePopover,
        subItemParent,
        handleParentMenuonClick: onClick,
      })
  );

  return (
    <div id={id} className={menuClass} style={style} ref={menuDomRef}>
      <div
        className="cursor-pointer"
        role="menu"
        onClick={togglePopover}
        onKeyDown={onKeyDown}
        tabIndex={0}
      >
        {anchorElement}
      </div>
      <div className={menuListClass}>{childrenWithFuncs}</div>
    </div>
  );
};

Menu.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  anchorElement: PropTypes.node.isRequired,
  position: PropTypes.oneOf([
    "right-start",
    "right-end",
    "left-start",
    "left-end",
    "bottom-start",
    "bottom-end",
    "top-start",
    "top-end",
    "notification",
  ]),
  target: PropTypes.oneOf([
    "top-bottom",
    "bottom-top",
    "right-left",
    "left-right",
  ]),
  subItemParent: PropTypes.bool,
  closeMainMenu: PropTypes.func,
  keyHandler: PropTypes.func,
  style: PropTypes.object,
  children: PropTypes.array.isRequired,
  onClick: PropTypes.func,
};

export default Menu;
